/**
 * Single source of truth for disconnect-confirmation copy across every
 * integration / account in the app. Add a new entry here and reference it from
 * a page via `<DisconnectConfirmDialog integration="..." />` — never inline the
 * copy on the page.
 *
 * Each entry follows one of two patterns, depending on what disconnect ACTUALLY
 * does on the backend for that integration — check before copying an existing
 * entry's wording to a new one:
 *   - Non-destructive (soft-disconnect / just revokes access): <specific
 *     consequence> until you reconnect. <data-reassurance, e.g. "already-synced
 *     data is preserved">.
 *   - Destructive (hard delete): <what gets permanently deleted> — this cannot
 *     be undone. <secondary consequence, e.g. automations stopping>.
 * Getting this wrong in either direction is a real, shipped bug we hit:
 * Instagram/Facebook/LinkedIn said "Historical data will be preserved" for
 * months after their disconnect became a hard delete (confirmed 2026-08-01).
 */
export interface DisconnectCopy {
  /** Default name shown in the title ("Disconnect {name}?"). Pages may override
   *  this at the call site for per-account names like "@handle". */
  name: string;
  /** Consequence + reversibility + data reassurance. */
  description: string;
}

export const DISCONNECT_COPY = {
  razorpay: {
    name: 'Razorpay',
    description:
      'Payment links, plans and subscriptions will stop working until you reconnect. You can reconnect anytime.',
  },
  payu: {
    name: 'PayU',
    description:
      'New payments through PayU — UPI, cards, and netbanking — will stop being accepted until you reconnect. Existing payment records are preserved.',
  },
  cashfree: {
    name: 'Cashfree',
    description:
      'New payments through Cashfree will stop being accepted until you reconnect. Existing payment records are preserved.',
  },
  woocommerce: {
    name: 'WooCommerce',
    description:
      'Product and order syncing with your WooCommerce store will stop until you reconnect. Already-synced data is preserved.',
  },
  uberdirect: {
    name: 'Uber Direct',
    description:
      "You won't be able to create or track Uber Direct deliveries until you reconnect. Existing delivery records are preserved.",
  },
  // Instagram / Facebook / LinkedIn disconnect a HARD delete on the backend
  // (InstagramRepository.cascadeDeleteAccount / FacebookRepository.deleteAccount
  // / LinkedIn's equivalent) — it permanently removes every post, scheduled
  // post, draft, inbox thread + message, automation flow, log and lead tied to
  // the account, confirmed 2026-08-01. This copy previously said "Historical
  // data will be preserved", which was true only during a brief soft-disconnect
  // period (is_active=0, no delete) that was reverted without updating this
  // file — do NOT restore that wording unless disconnect goes back to a
  // non-destructive soft-disconnect.
  instagram: {
    name: 'this account',
    description:
      'This permanently deletes every post, scheduled post, draft, and inbox conversation for this account — this cannot be undone. Automations tied to it will also stop working.',
  },
  facebook: {
    name: 'this page',
    description:
      'This permanently deletes every post, scheduled post, draft, and inbox conversation for this page — this cannot be undone. Automations tied to it will also stop working.',
  },
  linkedin: {
    name: 'this account',
    description:
      'This permanently deletes every scheduled post and draft for this account — this cannot be undone. Automations tied to it will also stop working.',
  },
  // YouTube's disconnect is DESTRUCTIVE too, and in one extra way the others
  // are not: `YouTubeOAuthService.disconnectChannel` revokes the refresh token
  // at Google (best-effort) BEFORE `cascadeDeleteChannel` removes the synced
  // videos, comments and comment-automation rules. Reconnecting therefore means
  // a fresh Google consent screen, not just a click here — say so.
  youtube: {
    name: 'this channel',
    description:
      "This permanently deletes every synced video, comment and comment-automation rule for this channel — this cannot be undone. WeNext's access is revoked at Google, so reconnecting means authorizing again.",
  },
  tiktok: {
    name: 'this account',
    description:
      'Scheduled posts and automations tied to this account will stop working until you reconnect. Historical data will be preserved.',
  },
  googleCalendar: {
    name: 'Google Calendar',
    description:
      'New bookings will stop syncing to your Google Calendar and real-time conflict checking will be disabled until you reconnect. Events already on your calendar are preserved.',
  },
  googleSheets: {
    name: 'Google Sheets',
    description:
      'Automatic syncing of your data to Google Sheets will stop until you reconnect. Data already in your spreadsheets is preserved.',
  },
  zoom: {
    name: 'Zoom',
    description:
      'New bookings will stop getting Zoom meeting links until you reconnect. Meetings already scheduled keep their existing links.',
  },
} satisfies Record<string, DisconnectCopy>;

export type DisconnectIntegration = keyof typeof DISCONNECT_COPY;
